<template>
    <div>
        <TransitionExpand>
            <div v-show="showInput" class="form-row">
                <div class="col form-group mb-0" :class="{ 'input-group': extended || hasMultiVoucherCookieCode }">
                    <!--Discount field is optional-->
                    <input
                        type="text"
                        name="code"
                        v-model.trim="code"
                        maxlength="20"
                        class="code form-control input-gray"
                        :placeholder="extended ? '' : $t('Generic.voucherCode')"
                        :readonly="hasMultiVoucherCookieCode"
                        @keypress.enter.stop.prevent="checkActivation($event.target.value)"
                        @change="!extended && checkActivation($event.target.value)"
                        ref="code"
                        :required="!extended"
                        :aria-label="$t('Generic.voucherCode')"
                    />

                    <div v-if="hasMultiVoucherCookieCode" class="input-group-append">
                        <button
                            v-if="hasMultiVoucherCookieCode"
                            type="button"
                            @click="removeMultiVoucherCode"
                            class="btn btn-secondary"
                        >
                            {{ $t('Generic.change') }}
                        </button>
                    </div>

                    <div v-else-if="extended" class="input-group-append">
                        <button class="btn btn-secondary" type="button" @click="checkActivation(code)">
                            {{ $t('Generic.redeem') }}
                        </button>
                    </div>

                    <small
                        v-for="(error, index) in errors"
                        :key="'discountError' + index"
                        class="form-text discount-errors"
                    >
                        {{ error }}
                    </small>
                </div>
            </div>
        </TransitionExpand>

        <!--Webshop extended controls-->
        <div v-if="extended">
            <div v-if="discountCoupon.value" class="discount-code-applied mt-2">
                <h4 class="section-header mb-0">{{ $t('Checkout.discount.discountUsed') }}:</h4>

                <div class="d-flex justify-content-between align-items-center">
                    <button
                        type="button"
                        v-tooltip="$t('Checkout.discount.removeDiscount')"
                        class="remove-code btn p-0"
                        @click="removeDiscountCoupon()"
                    >
                        <IconSvg filepath="close.svg" />
                    </button>
                    <code class="code text-danger">{{ discountCoupon.code }}</code>
                    <span class="value ml-auto">-{{ discountCoupon.value | formatPriceFilter }}</span>
                </div>
            </div>


            <div v-if="valueVouchersProxy.length" class="voucher-codes-applied mt-2">
                <h4 class="section-header mb-0">{{ $t('Checkout.discount.vouchersUsed') }}:</h4>

                <div
                    v-for="(voucher, index) in valueVouchersProxy"
                    :key="voucher.code"
                    class="d-flex justify-content-between align-items-center"
                >
                    <button
                        type="button"
                        v-tooltip="$t('Checkout.discount.removeVoucher')"
                        class="remove-code btn p-0"
                        @click="removeVoucher(index)"
                    >
                        <IconSvg filepath="close.svg" />
                    </button>
                    <code class="code text-danger">{{ voucher.code }}</code>
                    <span class="value ml-auto">-{{ voucher.value | formatPriceFilter }}</span>
                </div>
            </div>

            <div v-if="remainingVoucherCredit" class="remaining-voucher-credit mt-2">
                <span class="mb-0">{{ $t('Checkout.discount.balance') }}</span>

                <span class="value ml-auto">{{ remainingVoucherCredit | formatPriceFilter }}</span>
            </div>

            <Modal v-if="showActivationDialog" @close="closeDialog" :width="560">
                <h4 slot="header">{{ $t('Checkout.discount.activationRequired') }}</h4>

                <div>
                    <p>
                        {{
                            $t('Checkout.discount.activationText', {
                                amount: activationCodePattern.length,
                                pattern: activationCodePattern
                            })
                        }}
                    </p>
                    <div class="form-group">
                        <input
                            type="text"
                            name="activationCode"
                            v-model="activationCode"
                            :maxlength="activationCodePattern.length"
                            :minlength="activationCodePattern.length"
                            :placeholder="
                                $t('Checkout.discount.activation.placeholder', { pattern: activationCodePattern })
                            "
                            class="form-control input-gray"
                            required
                            ref="activationCode"
                            @keypress.enter.stop.prevent="checkCode"
                            :aria-label="
                                $t('Checkout.discount.activation.placeholder', { pattern: activationCodePattern })
                            "
                        />
                    </div>
                </div>

                <div slot="footer">
                    <button
                        type="button"
                        class="btn btn-secondary mr-3"
                        :disabled="activationCode.length !== activationCodePattern.length"
                        @click.stop="checkCode"
                    >
                        {{ $t('Generic.proceed') }}
                    </button>
                    <button type="button" class="btn btn-light" @click.stop="closeDialog">
                        {{ $t('Generic.abort') }}
                    </button>
                </div>
            </Modal>
        </div>

        <!--Multivoucher simplified activation code control-->
        <div v-else-if="activationRequiredVersion >= 1" class="form-group mt-3">
            <input
                type="text"
                name="activationCode"
                :value="activationCode"
                :maxlength="activationCodePattern.length"
                :minlength="activationCodePattern.length"
                :placeholder="$t('Checkout.discount.activation.placeholder', { pattern: activationCodePattern })"
                class="form-control input-gray"
                required
                ref="activationCode"
                @change="updateActivation($event.target.value)"
                :aria-label="$t('Checkout.discount.activation.placeholder', { pattern: activationCodePattern })"
            />
        </div>
    </div>
</template>

<script>
import Modal from '@/components/Modal';
import TransitionExpand from '@/components/utility/TransitionExpand';
import formatPriceFilter from '@/filters/formatPrice';

export default {
    name: 'Discount',
    filters: { formatPriceFilter },
    components: {
        Modal,
        TransitionExpand
    },
    data() {
        return {
            code: this.$cookies.vv || this.$cookies.multiVoucherCode || '',
            activationCode: '',
            activationRequiredVersion: null,
            showActivationDialog: false,
            errors: []
        };
    },
    props: {
        extended: Boolean,
        email: String,
        productVariantId: [String, Number],
        totalProductPrice: Number,
        showInput: {
            type: Boolean,
            default: true
        },
        remainingVoucherCredit: Number,

        discountCoupon: {
            type: Object,
            default() {
                return {};
            }
        },
        valueVouchers: Array
    },
    computed: {
        valueVouchersProxy() {
            /**
             * Even though it seems to return a new array each time it is called, don't forget that computed props
             * are cached (in a browser) until any of the properties it is dependent on gets changed
             */
            return [...this.valueVouchers];
        },
        activationCodePattern() {
            if (this.activationRequiredVersion === 1) {
                return 'XXX-XXX-XXX';
            } else if (this.activationRequiredVersion === 2) {
                return 'XXXX';
            }
            return '';
        },
        hasMultiVoucherCookieCode() {
            return !!this.$store.state.multiVoucherCode;
        }
    },
    methods: {
        closeDialog() {
            this.showActivationDialog = false;
        },
        async checkActivation(code) {
            if (!code) {
                return;
            }
            this.activationRequiredVersion = null;

            try {
                const { data } = await this.$axios.post('/shop/activation-required', { code });

                this.activationRequiredVersion = data.version;

                data.activationRequired && (this.activationCode = '');

                if (this.extended) {
                    if (data.activationRequired) {
                        this.showActivationDialog = true;
                    } else {
                        this.checkCode();
                    }
                }
                //multivoucher single input case without activation
                else {
                    this.valueVouchersProxy.splice(0, 1);
                    this.valueVouchersProxy.push({
                        code
                    });
                    this.$emit('update:valueVouchers', this.valueVouchersProxy);
                }
            } catch (e) {}
        },
        async checkCode() {
            this.errors = [];
            const { activationCode, email, productVariantId, totalProductPrice } = this;

            try {
                //code should not be overwritten after response, therefore code: this.code
                const { data } = await this.$axios.post('/shop/check-code', {
                    code: this.code,
                    totalProductPrice,
                    email,
                    productVariantId
                });

                const { code, value, type, notCombinable } = data;

                if (data.type === 'discount') {
                    this.$emit('update:discountCoupon', {
                        code,
                        value,
                        activationCode
                    });
                } else if (type === 'voucher') {
                    if (!this.valueVouchersProxy.find(voucher => voucher.code === code)) {
                        this.valueVouchersProxy.push({
                            code,
                            value,
                            activationCode
                        });
                        this.$emit('update:valueVouchers', this.valueVouchersProxy);
                    }
                }

                this.code = '';
            } catch (error) {
                this.errors = error.response.data.map(error => {
                    return this.$t(error.text);
                });
            } finally {
                /**
                 * Very important! this.$emit in try block has an async nature and gets executed later than finally.
                 * Therefore reactive DOM changes from finally overwrite pending $emit reactive changes from try.
                 * $nextTick to the rescue
                 */
                await this.$nextTick();
                this.closeDialog();
            }
        },
        updateActivation(activationCode) {
            this.valueVouchersProxy[0].activationCode = activationCode;
            this.$emit('update:valueVouchers', this.valueVouchersProxy);
        },
        removeVoucher(index) {
            this.valueVouchersProxy.splice(index, 1);
            this.$emit('update:valueVouchers', this.valueVouchersProxy);
        },
        removeDiscountCoupon() {
            this.$emit('update:discountCoupon', {});
        },
        removeMultiVoucherCode() {
            this.$store.dispatch('setMultiVoucherCode', null);
            //instead of this.$router.replace, location.replace will init SSR with all necessary 401 checks
            location.replace('/');
        }
    },
    mounted() {
        this.checkActivation(this.code);
    },
    watch: {
        productVariantId() {
            if (this.code) {
                this.checkCode();
            }
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/bootstrap/variables' as bsvars;
@use '@/styles/variables';

.voucher-codes-applied,
.discount-code-applied {
    button.remove-code {
        line-height: 1;

        .icon-close {
            color: bsvars.$text-muted;
            font-size: 14px;
            margin-right: 10px;
        }
    }

    .code {
        font-weight: bold;
        font-size: 14px;
    }
}

.remaining-voucher-credit {
    display: flex;
    align-items: center;
}

.discount-errors {
    color: red;
    width: 100%;
    flex: 1 0 auto;
}

@include variables.ie {
    .form-row {
        position: static !important;
        visibility: visible !important;
    }
}
</style>
